import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import DocumentUpload from './components/DocumentUpload';
import DocumentList from './components/DocumentList';
import DocumentPreview from './components/DocumentPreview';
import { getAllDocuments } from '../../services/documentService';

/**
 * Document Management Portal
 * PCF Admin interface for uploading and managing program documents for RAG chatbot
 */
const DocumentManagementPortal = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const docs = await getAllDocuments();
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    setShowUpload(false);
    loadDocuments();
  };

  const handleDocumentSelect = (doc) => {
    setSelectedDocument(doc);
  };

  const handleDocumentDeleted = () => {
    setSelectedDocument(null);
    loadDocuments();
  };

  // Filter documents based on search
  const filteredDocuments = documents?.filter(doc =>
    doc?.file_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.file_type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    doc?.processing_status?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  // Statistics
  const stats = {
    total: documents?.length || 0,
    completed: documents?.filter(d => d?.processing_status === 'completed')?.length || 0,
    processing: documents?.filter(d => d?.processing_status === 'processing')?.length || 0,
    failed: documents?.filter(d => d?.processing_status === 'failed')?.length || 0,
    totalChunks: documents?.reduce((sum, doc) => sum + (doc?.chunk_count || 0), 0)
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Document Management Portal</h1>
              <p className="text-muted-foreground">
                Upload and manage program documents for the AI chatbot knowledge base
              </p>
            </div>
            <Button
              onClick={() => setShowUpload(true)}
              className="flex items-center space-x-2"
            >
              <Icon name="Upload" size={20} />
              <span>Upload Document</span>
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Documents</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="FileText" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-success">{stats?.completed}</p>
                </div>
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Processing</p>
                  <p className="text-2xl font-bold text-warning">{stats?.processing}</p>
                </div>
                <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center">
                  <Icon name="Loader" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-error">{stats?.failed}</p>
                </div>
                <div className="w-12 h-12 bg-error/10 rounded-full flex items-center justify-center">
                  <Icon name="XCircle" size={24} className="text-error" />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Chunks</p>
                  <p className="text-2xl font-bold text-foreground">{stats?.totalChunks}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Database" size={24} className="text-primary" />
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Icon 
              name="Search" 
              size={20} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search documents by name, type, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document List */}
          <div className="lg:col-span-2">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4 flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-error" />
                <p className="text-error text-sm">{error}</p>
              </div>
            )}

            <DocumentList
              documents={filteredDocuments}
              isLoading={isLoading}
              onDocumentSelect={handleDocumentSelect}
              selectedDocument={selectedDocument}
              onRefresh={loadDocuments}
            />
          </div>

          {/* Document Preview Panel */}
          <div className="lg:col-span-1">
            {selectedDocument ? (
              <DocumentPreview
                document={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                onDeleted={handleDocumentDeleted}
                onRefresh={loadDocuments}
              />
            ) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">
                  Select a document to view details
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      {/* Upload Modal */}
      {showUpload && (
        <DocumentUpload
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
          userId={user?.id}
        />
      )}
    </div>
  );
};

export default DocumentManagementPortal;